import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import PublicHome from "@/pages/PublicHome";
import JobSearch from "@/pages/JobSearch";
import JobDetail from "@/pages/JobDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import AdminLogin from "@/pages/AdminLogin";
import CandidateDashboard from "@/pages/CandidateDashboard";
import CandidateProfile from "@/pages/CandidateProfile";
import Apply from "@/pages/Apply";
import Assessment from "@/pages/Assessment";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminApplications from "@/pages/AdminApplications";
import AdminApplicationDetail from "@/pages/AdminApplicationDetail";
import AdminJobs from "@/pages/AdminJobs";
import AdminJDGenerator from "@/pages/AdminJDGenerator";
import AdminCareerUpdates from "@/pages/AdminCareerUpdates";
import AdminTechnologies from "@/pages/AdminTechnologies";
import AdminInterviews from "@/pages/AdminInterviews";
import AdminAIQuestions from "@/pages/AdminAIQuestions";
import AdminVSAISettings from "@/pages/AdminVSAISettings";
import AdminEmailLogs from "@/pages/AdminEmailLogs";
import AdminAuditLogs from "@/pages/AdminAuditLogs";
import AdminSupport from "@/pages/AdminSupport";
import AdminCareerEvents from "@/pages/AdminCareerEvents";
import Support from "@/pages/Support";
import CareerEvents, { CareerEventDetail } from "@/pages/CareerEvents";
import RecruiterDashboard from "@/pages/RecruiterDashboard";
import RecruiterApplications from "@/pages/RecruiterApplications";
import { HackathonsList, HackathonDetail } from "@/pages/Hackathons";
import { loadUser } from "@/lib/api";
import CandidateHubLayout, {
  CareerHome, CompanyDirectory, ExternalOpportunities, ExperienceHub,
  CareerResources as HubResources, PracticeChallenges, MyProjects,
  MyCertifications, CareerJourney, CareerRoadmap,
} from "@/pages/CandidateHub";

function RequireRole({ roles, children }) {
  const user = loadUser();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    // Send unauthorised users to their own home — NEVER default to /admin/login.
    if (user.role === "candidate") return <Navigate to="/candidate/hub" replace />;
    if (user.role === "recruiter") return <Navigate to="/recruiter/dashboard" replace />;
    if (user.role === "hr_admin") return <Navigate to="/admin/dashboard" replace />;
    if (user.role === "super_admin") return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<PublicHome />} />
        <Route path="/jobs" element={<JobSearch />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/hackathons" element={<HackathonsList />} />
        <Route path="/hackathons/:id" element={<HackathonDetail />} />
        <Route path="/career-events" element={<CareerEvents />} />
        <Route path="/career-events/:id" element={<CareerEventDetail />} />
        <Route path="/support" element={<Support />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/candidate/verify-email" element={<VerifyEmail />} />
        <Route path="/admin/login" element={<AdminLogin />} />

        <Route path="/candidate/dashboard" element={<RequireRole roles={["candidate"]}><CandidateDashboard /></RequireRole>} />
        <Route path="/candidate/profile" element={<RequireRole roles={["candidate"]}><CandidateProfile /></RequireRole>} />
        <Route path="/apply/:jobId" element={<RequireRole roles={["candidate"]}><Apply /></RequireRole>} />
        <Route path="/assessment/:aid" element={<RequireRole roles={["candidate"]}><Assessment /></RequireRole>} />

        {/* VS Career & Experience Hub */}
        <Route path="/candidate/hub" element={<RequireRole roles={["candidate"]}><CareerHome /></RequireRole>} />
        <Route path="/candidate/hub/companies" element={<RequireRole roles={["candidate"]}><CompanyDirectory /></RequireRole>} />
        <Route path="/candidate/hub/external" element={<RequireRole roles={["candidate"]}><ExternalOpportunities /></RequireRole>} />
        <Route path="/candidate/hub/experience" element={<RequireRole roles={["candidate"]}><ExperienceHub /></RequireRole>} />
        <Route path="/candidate/hub/practice" element={<RequireRole roles={["candidate"]}><PracticeChallenges /></RequireRole>} />
        <Route path="/candidate/hub/resources" element={<RequireRole roles={["candidate"]}><HubResources /></RequireRole>} />
        <Route path="/candidate/hub/projects" element={<RequireRole roles={["candidate"]}><MyProjects /></RequireRole>} />
        <Route path="/candidate/hub/certifications" element={<RequireRole roles={["candidate"]}><MyCertifications /></RequireRole>} />
        <Route path="/candidate/hub/journey" element={<RequireRole roles={["candidate"]}><CareerJourney /></RequireRole>} />
        <Route path="/candidate/hub/roadmap" element={<RequireRole roles={["candidate"]}><CareerRoadmap /></RequireRole>} />

        <Route path="/admin/dashboard" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminDashboard /></RequireRole>} />
        <Route path="/admin/applications" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminApplications /></RequireRole>} />
        <Route path="/admin/applications/:id" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminApplicationDetail /></RequireRole>} />
        <Route path="/admin/jobs" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminJobs /></RequireRole>} />
        <Route path="/admin/interviews" element={<RequireRole roles={["super_admin","hr_admin","recruiter"]}><AdminInterviews /></RequireRole>} />
        <Route path="/admin/ai-questions" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminAIQuestions /></RequireRole>} />
        <Route path="/admin/jd-generator" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminJDGenerator /></RequireRole>} />
        <Route path="/admin/career-updates" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminCareerUpdates /></RequireRole>} />
        <Route path="/admin/technologies" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminTechnologies /></RequireRole>} />
        <Route path="/admin/career-domains" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminTechnologies /></RequireRole>} />
        <Route path="/admin/email-logs" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminEmailLogs /></RequireRole>} />
        <Route path="/admin/support" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminSupport /></RequireRole>} />
        <Route path="/admin/career-events" element={<RequireRole roles={["super_admin","hr_admin"]}><AdminCareerEvents /></RequireRole>} />
        <Route path="/admin/settings/vs-ai" element={<RequireRole roles={["super_admin"]}><AdminVSAISettings /></RequireRole>} />
        <Route path="/admin/audit-logs" element={<RequireRole roles={["super_admin"]}><AdminAuditLogs /></RequireRole>} />

        <Route path="/recruiter/dashboard" element={<RequireRole roles={["recruiter"]}><RecruiterDashboard /></RequireRole>} />
        <Route path="/recruiter/applications" element={<RequireRole roles={["recruiter"]}><RecruiterApplications /></RequireRole>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
